import React, { useState, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import AppShell from "@/components/AppShell";
import {
  Upload, File as FileIcon, CheckCircle, Warning, ArrowRight, Spinner, X,
  Buildings, AddressBook,
} from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * ImportGestionale (iter 240 v2) — Import da file di export del gestionale.
 *
 * 2 tab:
 *   1) "Immobili" — upload XML del gestionale → preview tabellare → confirm.
 *   2) "Contatti / rubrica" — 2 sotto-percorsi:
 *      a) Contatti embedded nello stesso file XML degli immobili (se il
 *         gestionale li esporta insieme — capita su Wikicasa MLS e altri).
 *      b) File separato: CSV o vCard (formato standard rubrica) — usa
 *         endpoint già esistenti /api/contacts/import-{csv,vcard}.
 *
 * Decisione user iter 240: i 2 import vivono nella STESSA pagina.
 * Se nel file XML degli immobili sono già presenti i contatti, l'UI li
 * mostra automaticamente nel tab Contatti.
 */
export default function ImportGestionale() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("properties"); // "properties" | "contacts"

  // Shared XML state (vale per properties + contatti embedded)
  const fileRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [selectedProps, setSelectedProps] = useState(new Set());
  const [importingProps, setImportingProps] = useState(false);
  const [propsImported, setPropsImported] = useState(false);

  // Contatti embedded nello stesso XML
  const [selectedContacts, setSelectedContacts] = useState(new Set());
  const [importingContacts, setImportingContacts] = useState(false);
  const [contactsImported, setContactsImported] = useState(false);

  // Auto-match risultati post-import (iter 240 C)
  const [autoMatches, setAutoMatches] = useState([]);  // [{property_id, property_title, matches: [{name, phone, score, reasons}]}]
  const [contactAutoMatches, setContactAutoMatches] = useState([]);  // [{contact_id, contact_name, matches: [{title, price, score}]}]

  // CSV/VCF file (rubrica separata)
  const contactFileRef = useRef(null);
  const [contactDragOver, setContactDragOver] = useState(false);
  const [contactUploadResult, setContactUploadResult] = useState(null);
  const [contactUploading, setContactUploading] = useState(false);

  const handleXmlFile = async (fileOrFiles) => {
    // Accetta sia un singolo File che FileList (multi-upload).
    const list = Array.isArray(fileOrFiles)
      ? fileOrFiles
      : fileOrFiles instanceof FileList
        ? Array.from(fileOrFiles)
        : fileOrFiles
          ? [fileOrFiles]
          : [];
    if (list.length === 0) return;
    for (const f of list) {
      if (!f.name.toLowerCase().endsWith(".xml")) {
        toast.error(`"${f.name}": carica solo file .xml`);
        return;
      }
      if (f.size > 10 * 1024 * 1024) {
        toast.error(`"${f.name}" troppo grande (max 10 MB)`);
        return;
      }
    }
    setUploading(true);
    const form = new FormData();
    if (list.length === 1) {
      form.append("file", list[0]);
    } else {
      for (const f of list) form.append("files", f);
    }
    try {
      const { data } = await api.post("/properties/import-xml/preview", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
      setSelectedProps(new Set(data.items.map((_, i) => i)));
      setSelectedContacts(new Set((data.contacts || []).map((_, i) => i)));
      setPropsImported(false);
      setContactsImported(false);
      setAutoMatches([]);
      setContactAutoMatches([]);
      if (data.found === 0 && (data.contacts_found || 0) === 0) {
        toast.warning("Nessun immobile né contatto trovato nei file");
      } else {
        const parts = [];
        if (data.found > 0) parts.push(`${data.found} immobili`);
        if ((data.contacts_found || 0) > 0) parts.push(`${data.contacts_found} contatti`);
        const filePart = data.files_count > 1 ? ` da ${data.files_count} file` : "";
        toast.success(`${parts.join(" · ")}${filePart} · ${data.format_detected}`);
        if ((data.contacts_found || 0) > 0 && data.found === 0) {
          setActiveTab("contacts");
        }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nel parsing del file");
    } finally {
      setUploading(false);
    }
  };

  const toggle = (setter, current, i) => {
    const ns = new Set(current);
    if (ns.has(i)) ns.delete(i); else ns.add(i);
    setter(ns);
  };

  const toggleAllProps = () => {
    if (!preview) return;
    setSelectedProps(
      selectedProps.size === preview.items.length ? new Set() : new Set(preview.items.map((_, i) => i)),
    );
  };
  const toggleAllContacts = () => {
    if (!preview) return;
    setSelectedContacts(
      selectedContacts.size === (preview.contacts || []).length ? new Set() : new Set((preview.contacts || []).map((_, i) => i)),
    );
  };

  const totalPhotos = useMemo(() => {
    if (!preview) return 0;
    return Array.from(selectedProps).reduce((acc, i) => acc + (preview.items[i]?.photos?.length || 0), 0);
  }, [selectedProps, preview]);

  const submitProperties = async () => {
    if (!preview || selectedProps.size === 0) return;
    setImportingProps(true);
    try {
      const { data } = await api.post("/properties/import-xml/confirm", {
        import_id: preview.import_id,
        selected_indexes: Array.from(selectedProps).sort((a, b) => a - b),
      });
      const n = data.created_count || 0;
      if (n > 0) {
        toast.success(`Importati ${n} immobili nella tua bacheca`);
        setPropsImported(true);
        setAutoMatches(data.auto_matches || []);
        if (data.errors?.length) toast.warning(`${data.errors.length} immobili saltati`);
      } else {
        toast.error(data.errors?.[0]?.reason || "Nessun immobile importato");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore durante l'import");
    } finally {
      setImportingProps(false);
    }
  };

  const submitContactsFromXml = async () => {
    if (!preview || selectedContacts.size === 0) return;
    setImportingContacts(true);
    try {
      const { data } = await api.post("/properties/import-xml/confirm-contacts", {
        import_id: preview.import_id,
        selected_indexes: Array.from(selectedContacts).sort((a, b) => a - b),
      });
      const n = data.inserted || 0;
      if (n > 0) {
        toast.success(`Importati ${n} contatti nella rubrica`);
        setContactsImported(true);
        setContactAutoMatches(data.auto_matches || []);
        if (data.skipped > 0) toast.info(`${data.skipped} contatti saltati (duplicati o vuoti)`);
      } else {
        toast.warning("Nessun contatto importato (forse erano già in rubrica)");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore durante l'import contatti");
    } finally {
      setImportingContacts(false);
    }
  };

  const handleContactFile = async (file) => {
    if (!file) return;
    const fname = file.name.toLowerCase();
    const isCsv = fname.endsWith(".csv");
    const isVcf = fname.endsWith(".vcf");
    const isXlsx = fname.endsWith(".xlsx") || fname.endsWith(".xls") || fname.endsWith(".xlsm");
    if (!isCsv && !isVcf && !isXlsx) {
      toast.error("Carica un file .csv, .vcf o .xlsx");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File troppo grande (max 10 MB)");
      return;
    }
    setContactUploading(true);
    const form = new FormData();
    form.append("file", file);
    if (isCsv) {
      // Heuristic: most common column names in IT gestionale CSV exports
      form.append("name_col", "name");
      form.append("phone_col", "phone");
      form.append("email_col", "email");
    }
    try {
      // iter v28 r300 — Excel ha auto-detect colonne, niente mapping manuale.
      const url = isVcf ? "/contacts/import-vcard"
        : isXlsx ? "/contacts/import-excel"
        : "/contacts/import-csv";
      const { data } = await api.post(url, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setContactUploadResult(data);
      const n = data.inserted || 0;
      if (n > 0) {
        toast.success(`Importati ${n} contatti nella rubrica`);
      } else {
        toast.warning("Nessun contatto importato (forse erano già in rubrica)");
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || "Errore durante l'import";
      toast.error(detail);
      if (typeof detail === "string" && detail.toLowerCase().includes("colonna") || detail.toLowerCase?.().includes("name_col")) {
        toast.info("Verifica che il CSV abbia colonne 'name', 'phone', 'email'");
      }
    } finally {
      setContactUploading(false);
    }
  };

  const reset = () => {
    setPreview(null);
    setSelectedProps(new Set());
    setSelectedContacts(new Set());
    setPropsImported(false);
    setContactsImported(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const propsCount = preview?.items?.length || 0;
  const contactsCount = preview?.contacts?.length || 0;

  return (
    <AppShell title="Importa da gestionale" breadcrumb="Importa da gestionale">
      <div className="max-w-4xl mx-auto pb-24 px-4 sm:px-6">
        {/* HEADER */}
        <div className="mb-6">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#7C8B6E] font-bold mb-1.5">
            Importa da gestionale
          </div>
          <h1 className="font-editorial text-[28px] sm:text-[34px] leading-[1.1] tracking-tight text-[#1C1C1A]">
            Il tuo gestionale dentro Pronto, <span className="italic text-[#7C8B6E]">in 30 secondi.</span>
          </h1>
          <p className="mt-3 text-[14px] text-[#3A3A36] leading-relaxed max-w-2xl">
            Esporta i file dal tuo gestionale e portali dentro Pronto: immobili, contatti, ricerche dei
            clienti. Niente è importato finché tu non confermi.
          </p>
        </div>

        {/* TABS */}
        <div className="flex gap-1 mb-6 bg-[#F5F4F0] border border-[#E8E5DE] rounded-lg p-1 w-fit" data-testid="import-tabs">
          <button
            onClick={() => setActiveTab("properties")}
            className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-md text-[12.5px] font-medium transition-all ${
              activeTab === "properties" ? "bg-white text-[#1C1C1A] shadow-sm" : "text-[#6B6A65] hover:text-[#1C1C1A]"
            }`}
            data-testid="import-tab-properties"
          >
            <Buildings size={14} weight={activeTab === "properties" ? "fill" : "regular"} />
            Immobili
            {propsCount > 0 && <span className="ml-1 text-[10px] text-[#7C8B6E]">· {propsCount}</span>}
          </button>
          <button
            onClick={() => setActiveTab("contacts")}
            className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-md text-[12.5px] font-medium transition-all ${
              activeTab === "contacts" ? "bg-white text-[#1C1C1A] shadow-sm" : "text-[#6B6A65] hover:text-[#1C1C1A]"
            }`}
            data-testid="import-tab-contacts"
          >
            <AddressBook size={14} weight={activeTab === "contacts" ? "fill" : "regular"} />
            Contatti
            {contactsCount > 0 && <span className="ml-1 text-[10px] text-[#7C8B6E]">· {contactsCount}</span>}
          </button>
        </div>

        {/* TAB · IMMOBILI */}
        {activeTab === "properties" && (
          <>
            {!preview && (
              <Dropzone
                accept=".xml"
                acceptLabel="file XML"
                dragOver={dragOver}
                setDragOver={setDragOver}
                uploading={uploading}
                onFile={handleXmlFile}
                inputRef={fileRef}
                hint="Trascina qui il file XML esportato dal gestionale · max 10 MB · puoi caricarne anche più di uno"
                tail="Compatibile con i tracciati MLS dei gestionali italiani più diffusi"
                testid="dropzone-xml"
                multiple
              />
            )}

            {preview && (
              <div className="space-y-4" data-testid="preview-properties">
                <Header
                  label={`Ho trovato ${preview.found} immobili`}
                  sublabel={`Formato: ${preview.format_detected}`}
                  skipped={preview.skipped}
                  onReset={reset}
                />
                {preview.items.length > 0 && !propsImported && (
                  <>
                    <Toolbar
                      total={preview.items.length}
                      selected={selectedProps.size}
                      onToggleAll={toggleAllProps}
                      extra={totalPhotos > 0 ? `${totalPhotos} foto totali` : null}
                    />
                    <div className="space-y-2">
                      {preview.items.map((it, i) => (
                        <PropertyRow
                          key={i}
                          item={it}
                          checked={selectedProps.has(i)}
                          onToggle={() => toggle(setSelectedProps, selectedProps, i)}
                          i={i}
                        />
                      ))}
                    </div>
                    <ConfirmBar
                      count={selectedProps.size}
                      noun="immobili"
                      onSubmit={submitProperties}
                      loading={importingProps}
                      testid="import-confirm-properties"
                    />
                  </>
                )}
                {propsImported && (
                  <>
                    <SuccessBanner
                      message={`${selectedProps.size} immobili importati nella tua bacheca`}
                      nextLabel={contactsCount > 0 ? "Passa ai contatti →" : "Vai agli annunci →"}
                      onNext={() => contactsCount > 0 ? setActiveTab("contacts") : navigate("/dashboard/annunci")}
                    />
                    {autoMatches.length > 0 && (
                      <PropertyAutoMatchPanel matches={autoMatches} navigate={navigate} />
                    )}
                  </>
                )}
              </div>
            )}
          </>
        )}

        {/* TAB · CONTATTI */}
        {activeTab === "contacts" && (
          <div className="space-y-6">
            {/* Caso A: contatti embedded nell'XML già caricato */}
            {preview && contactsCount > 0 && !contactsImported && (
              <div data-testid="contacts-embedded">
                <div className="mb-3 inline-flex items-center gap-2 px-3 py-1.5 bg-[#F1F4ED] border border-[#D8E0CC] rounded-full text-[12px] text-[#5C6A4F]">
                  <CheckCircle size={14} weight="fill" />
                  <span>Trovati <strong>{contactsCount}</strong> contatti nello stesso file degli immobili</span>
                </div>
                <Toolbar
                  total={contactsCount}
                  selected={selectedContacts.size}
                  onToggleAll={toggleAllContacts}
                />
                <div className="space-y-2 mt-3">
                  {preview.contacts.map((c, i) => (
                    <ContactRow
                      key={i}
                      contact={c}
                      checked={selectedContacts.has(i)}
                      onToggle={() => toggle(setSelectedContacts, selectedContacts, i)}
                      i={i}
                    />
                  ))}
                </div>
                <ConfirmBar
                  count={selectedContacts.size}
                  noun="contatti"
                  onSubmit={submitContactsFromXml}
                  loading={importingContacts}
                  testid="import-confirm-contacts-xml"
                />
              </div>
            )}

            {/* Caso A · esito */}
            {contactsImported && (
              <>
                <SuccessBanner
                  message={`${selectedContacts.size} contatti importati nella rubrica`}
                  nextLabel="Vai alla rubrica →"
                  onNext={() => navigate("/dashboard/contatti")}
                />
                {contactAutoMatches.length > 0 && (
                  <ContactAutoMatchPanel matches={contactAutoMatches} navigate={navigate} />
                )}
              </>
            )}

            {/* Caso B: nessun contatto nell'XML (o non ancora caricato) → upload CSV/VCF */}
            {(!preview || contactsCount === 0) && !contactUploadResult && (
              <div data-testid="contacts-standalone">
                <p className="text-[14px] text-[#3A3A36] leading-relaxed mb-4">
                  Se il tuo gestionale esporta la rubrica in un <strong>file separato</strong> (CSV o vCard),
                  caricalo qui sotto. Pronto importa nomi, telefoni, email e note.
                </p>
                <Dropzone
                  accept=".csv,.vcf,.xlsx,.xls,.xlsm"
                  acceptLabel="file Excel, CSV o vCard"
                  dragOver={contactDragOver}
                  setDragOver={setContactDragOver}
                  uploading={contactUploading}
                  onFile={handleContactFile}
                  inputRef={contactFileRef}
                  hint="Trascina qui il file della rubrica · CSV o vCard · max 10 MB"
                  tail="Su CSV: serve una colonna 'name', 'phone' (opzionale 'email')"
                  testid="dropzone-contacts"
                />
              </div>
            )}

            {/* Caso B · esito */}
            {contactUploadResult && (
              <SuccessBanner
                message={`${contactUploadResult.inserted} contatti importati ${contactUploadResult.skipped > 0 ? `· ${contactUploadResult.skipped} saltati (duplicati)` : ""}`}
                nextLabel="Vai alla rubrica →"
                onNext={() => navigate("/dashboard/contatti")}
              />
            )}
          </div>
        )}

        {/* HELP */}
        {!preview && !uploading && activeTab === "properties" && (
          <details className="mt-5 bg-white border border-[#EAE7DF] rounded-lg p-4 text-[12.5px] text-[#3A3A36]">
            <summary className="cursor-pointer font-semibold text-[#1C1C1A]">Non trovo l'export sul mio gestionale, dove sta?</summary>
            <div className="mt-3 space-y-2 leading-relaxed">
              <p>Ogni gestionale lo chiama in modo diverso, ma di solito è una di queste voci:</p>
              <ul className="ml-4 list-disc space-y-1 text-[#6B6A65]">
                <li>"Export XML" o "Esporta in XML"</li>
                <li>"Pubblica su portali" → spesso genera un XML scaricabile</li>
                <li>"Feed MLS" o "Tracciato MLS"</li>
                <li>"Esporta per Idealista / Immobiliare / Casa.it" — è lo stesso file</li>
              </ul>
              <p className="text-[#6B6A65] pt-1">Se il tuo gestionale esporta solo CSV o Excel, scrivici: aggiungiamo il supporto.</p>
            </div>
          </details>
        )}
      </div>
    </AppShell>
  );
}

// ── Sub-components ────────────────────────────────────────────────────

function Dropzone({ accept, acceptLabel, dragOver, setDragOver, uploading, onFile, inputRef, hint, tail, testid, multiple = false }) {
  return (
    <div
      data-testid={testid}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault(); setDragOver(false);
        const fs = Array.from(e.dataTransfer.files || []);
        if (fs.length === 0) return;
        onFile(multiple ? fs : fs[0]);
      }}
      onClick={() => inputRef.current?.click()}
      className={`relative cursor-pointer border-2 border-dashed rounded-xl p-10 sm:p-14 text-center transition-all ${
        dragOver
          ? "border-[#7C8B6E] bg-[#F1F4ED]"
          : "border-[#D8D5CC] bg-white hover:border-[#7C8B6E] hover:bg-[#FAFAF7]"
      }`}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        className="hidden"
        onChange={(e) => {
          const fs = Array.from(e.target.files || []);
          if (fs.length === 0) return;
          onFile(multiple ? fs : fs[0]);
        }}
      />
      {uploading ? (
        <div className="flex flex-col items-center gap-3">
          <Spinner size={32} className="animate-spin text-[#7C8B6E]" />
          <div className="text-[14px] text-[#3A3A36] font-medium">Leggo i file…</div>
          <div className="text-[11.5px] text-[#9A968B]">Restiamo in pagina, ci vogliono pochi secondi</div>
        </div>
      ) : (
        <>
          <div className="inline-flex w-14 h-14 rounded-full bg-[#F1F4ED] items-center justify-center mb-3">
            <Upload size={26} weight="duotone" className="text-[#7C8B6E]" />
          </div>
          <div className="text-[15px] font-semibold text-[#1C1C1A] mb-1">
            Trascina qui {multiple ? "uno o più" : "il"} {acceptLabel}
          </div>
          <div className="text-[12.5px] text-[#6B6A65] mb-5">{hint}</div>
          <div className="inline-flex items-center gap-1.5 text-[11.5px] text-[#9A968B]">
            <FileIcon size={14} weight="duotone" />
            {tail}
          </div>
        </>
      )}
    </div>
  );
}

function Header({ label, sublabel, skipped, onReset }) {
  return (
    <div className="bg-white border border-[#EAE7DF] rounded-xl p-4 sm:p-5 flex items-center gap-4">
      <div className="shrink-0 w-12 h-12 rounded-full bg-[#F1F4ED] flex items-center justify-center">
        <CheckCircle size={26} weight="fill" className="text-[#7C8B6E]" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[14px] font-semibold text-[#1C1C1A]">
          {label}
          {skipped > 0 && <span className="text-[#9A968B] text-[12px] font-normal"> · saltati {skipped}</span>}
        </div>
        <div className="text-[11.5px] text-[#6B6A65]">{sublabel}</div>
      </div>
      <button onClick={onReset} className="text-[11.5px] text-[#6B6A65] hover:text-[#1C1C1A] inline-flex items-center gap-1">
        <X size={14} /> Carica un altro file
      </button>
    </div>
  );
}

function Toolbar({ total, selected, onToggleAll, extra }) {
  return (
    <div className="flex items-center justify-between flex-wrap gap-2">
      <button onClick={onToggleAll} className="text-[12px] text-[#7C8B6E] hover:text-[#5C6A4F] font-medium">
        {selected === total ? "Deseleziona tutti" : "Seleziona tutti"}
      </button>
      <div className="text-[11.5px] text-[#6B6A65]">
        <strong className="text-[#1C1C1A]">{selected}</strong> selezionati
        {extra ? <span> · {extra}</span> : null}
      </div>
    </div>
  );
}

function PropertyRow({ item, checked, onToggle, i }) {
  return (
    <label
      className={`flex items-start gap-3 p-3.5 sm:p-4 rounded-lg border cursor-pointer transition-all ${
        checked ? "bg-[#F8FAF4] border-[#A8B89A]" : "bg-white border-[#EAE7DF] hover:border-[#C5C2B8]"
      }`}
      data-testid={`import-property-${i}`}
    >
      <input type="checkbox" checked={checked} onChange={onToggle} className="mt-1 w-4 h-4 accent-[#7C8B6E]" />
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-[14px] text-[#1C1C1A] truncate">{item.title}</div>
        <div className="mt-1 text-[12px] text-[#6B6A65] flex flex-wrap items-center gap-x-3 gap-y-0.5">
          {item.address && <span>{item.address}</span>}
          {item.city && !item.address && <span>{item.city}</span>}
          {item.surface && <span>· {item.surface} m²</span>}
          {item.rooms && <span>· {item.rooms} locali</span>}
          {item.bathrooms && <span>· {item.bathrooms} bagni</span>}
          {item.price && <span className="text-[#5C6A4F] font-semibold">· € {item.price.toLocaleString("it-IT")}</span>}
        </div>
        <div className="mt-1.5 text-[11px] text-[#9A968B] flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1 bg-[#F5F4F0] px-2 py-0.5 rounded">
            {item.property_type} · {item.listing_type}
          </span>
          {item.energy_class && (
            <span className="inline-flex items-center gap-1 bg-[#F5F4F0] px-2 py-0.5 rounded">Classe {item.energy_class}</span>
          )}
          {item.photos && item.photos.length > 0 && <span className="text-[#7C8B6E]">{item.photos.length} foto</span>}
        </div>
      </div>
    </label>
  );
}

function ContactRow({ contact, checked, onToggle, i }) {
  return (
    <label
      className={`flex items-start gap-3 p-3.5 rounded-lg border cursor-pointer transition-all ${
        checked ? "bg-[#F8FAF4] border-[#A8B89A]" : "bg-white border-[#EAE7DF] hover:border-[#C5C2B8]"
      }`}
      data-testid={`import-contact-${i}`}
    >
      <input type="checkbox" checked={checked} onChange={onToggle} className="mt-1 w-4 h-4 accent-[#7C8B6E]" />
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-[14px] text-[#1C1C1A] truncate">{contact.name}</div>
        <div className="mt-1 text-[12px] text-[#6B6A65] flex flex-wrap items-center gap-x-3 gap-y-0.5">
          {contact.phone && <span>{contact.phone}</span>}
          {contact.email && <span>· {contact.email}</span>}
        </div>
        {contact.notes && <div className="mt-1 text-[11.5px] text-[#9A968B] line-clamp-2">{contact.notes}</div>}
      </div>
    </label>
  );
}

function ConfirmBar({ count, noun, onSubmit, loading, testid }) {
  return (
    <div className="sticky bottom-4 bg-white border border-[#EAE7DF] shadow-lg rounded-xl p-4 flex items-center justify-between gap-3">
      <div className="text-[12.5px] text-[#3A3A36]">
        Stai per importare <strong>{count}</strong> {noun}.
        <span className="text-[#9A968B] block sm:inline sm:ml-1">Nessuna modifica al tuo gestionale.</span>
      </div>
      <button
        onClick={onSubmit}
        disabled={count === 0 || loading}
        className="bg-[#1C1C1A] text-white hover:bg-[#000] disabled:opacity-50 disabled:cursor-not-allowed text-[13px] font-semibold px-5 py-2.5 rounded-full inline-flex items-center gap-1.5 shrink-0 transition-colors"
        data-testid={testid}
      >
        {loading ? <Spinner size={14} className="animate-spin" /> : <ArrowRight size={14} weight="bold" />}
        Importa {count}
      </button>
    </div>
  );
}

function SuccessBanner({ message, nextLabel, onNext }) {
  return (
    <div className="bg-[#F1F4ED] border border-[#A8B89A] rounded-xl p-5 flex items-center gap-4" data-testid="import-success">
      <CheckCircle size={28} weight="fill" className="text-[#5C6A4F] shrink-0" />
      <div className="flex-1 text-[14px] text-[#1C1C1A] font-medium">{message}</div>
      <button
        onClick={onNext}
        className="bg-[#1C1C1A] text-white hover:bg-[#000] text-[12.5px] font-semibold px-4 py-2 rounded-full inline-flex items-center gap-1.5 shrink-0 transition-colors"
        data-testid="import-success-next"
      >
        {nextLabel}
      </button>
    </div>
  );
}

function PropertyAutoMatchPanel({ matches, navigate }) {
  // matches: [{property_id, property_title, matches: [{contact_id, name, phone, score, reasons}]}]
  const totalHits = matches.reduce((acc, m) => acc + (m.matches?.length || 0), 0);
  return (
    <div className="bg-white border border-[#E8DBA8] rounded-xl p-5" data-testid="auto-match-properties">
      <div className="flex items-center gap-2 mb-3">
        <Warning size={18} weight="fill" className="text-[#C8A66B]" />
        <h3 className="font-semibold text-[14.5px] text-[#1C1C1A]">
          {totalHits} contatti li stavano già cercando
        </h3>
      </div>
      <p className="text-[12.5px] text-[#6B6A65] mb-4 leading-relaxed">
        Pronto ha incrociato gli immobili appena importati con la tua rubrica.
        Ecco chi potrebbe essere interessato — un messaggio WhatsApp è già pronto.
      </p>
      <div className="space-y-3">
        {matches.map((group) => (
          <div key={group.property_id} className="border-t border-[#EAE7DF] pt-3 first:border-t-0 first:pt-0">
            <div className="text-[12.5px] font-semibold text-[#1C1C1A] mb-1.5 truncate">
              {group.property_title}
            </div>
            <div className="space-y-1.5">
              {group.matches.map((m) => (
                <div key={m.contact_id} className="flex items-center justify-between gap-2 text-[12px]">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-[#5C6A4F] font-semibold w-7 text-right shrink-0">{m.score}%</span>
                    <span className="text-[#1C1C1A] truncate">{m.name}</span>
                    <span className="text-[#9A968B] text-[11px] truncate">· {m.reasons.join(", ")}</span>
                  </div>
                  <button
                    onClick={() => navigate(`/dashboard/property/${group.property_id}?tab=matches`)}
                    className="text-[11px] text-[#7C8B6E] hover:text-[#5C6A4F] shrink-0 font-medium"
                    data-testid={`auto-match-go-${m.contact_id}`}
                  >
                    Apri →
                  </button>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ContactAutoMatchPanel({ matches, navigate }) {
  // matches: [{contact_id, contact_name, matches: [{property_id, title, price, score, reasons}]}]
  const totalHits = matches.reduce((acc, m) => acc + (m.matches?.length || 0), 0);
  return (
    <div className="bg-white border border-[#E8DBA8] rounded-xl p-5" data-testid="auto-match-contacts">
      <div className="flex items-center gap-2 mb-3">
        <Warning size={18} weight="fill" className="text-[#C8A66B]" />
        <h3 className="font-semibold text-[14.5px] text-[#1C1C1A]">
          Hai già {totalHits} immobili in portafoglio che fanno al caso loro
        </h3>
      </div>
      <p className="text-[12.5px] text-[#6B6A65] mb-4 leading-relaxed">
        Pronto ha incrociato i contatti appena importati con i tuoi immobili.
        Eccoti i match più forti — uno per uno.
      </p>
      <div className="space-y-3">
        {matches.map((group) => (
          <div key={group.contact_id} className="border-t border-[#EAE7DF] pt-3 first:border-t-0 first:pt-0">
            <div className="text-[12.5px] font-semibold text-[#1C1C1A] mb-1.5 truncate">
              {group.contact_name}
            </div>
            <div className="space-y-1.5">
              {group.matches.map((m) => (
                <div key={m.property_id} className="flex items-center justify-between gap-2 text-[12px]">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-[#5C6A4F] font-semibold w-7 text-right shrink-0">{m.score}%</span>
                    <span className="text-[#1C1C1A] truncate">{m.title}</span>
                    {m.price && (
                      <span className="text-[#9A968B] text-[11px] shrink-0">· € {m.price.toLocaleString("it-IT")}</span>
                    )}
                  </div>
                  <button
                    onClick={() => navigate(`/dashboard/property/${m.property_id}?tab=matches`)}
                    className="text-[11px] text-[#7C8B6E] hover:text-[#5C6A4F] shrink-0 font-medium"
                    data-testid={`auto-match-prop-${m.property_id}`}
                  >
                    Apri →
                  </button>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
